/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

import {
    IIpAddrMatch,
    MatchOperation,
} from 'generated-types';
import {
    IpAddrConfigItem,
    IpAddrPrefixConfigItem,
    IpAddrRangeConfigItem,
    MessageItem,
    RepeatedMessageItem,
} from 'ajs/modules/data-model/factories';
import { StringService } from 'string-service';
import { TEnumValueLabelsHash } from 'ajs/modules/core/services/schema-service';
import { L10nService } from '@vmw/ngx-vip';

import * as l10n from '../match.l10n';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;

export const IP_ADDR_MATCH_CONFIG_ITEM_TOKEN = 'IpAddrMatchConfigItem';

type TIpAddrMatchPartial = Omit<IIpAddrMatch, 'addrs' | 'ranges' | 'prefixes'>;

interface IIpAddrMatchConfig extends TIpAddrMatchPartial {
    addrs?: RepeatedMessageItem<IpAddrConfigItem>;
    ranges?: RepeatedMessageItem<IpAddrRangeConfigItem>;
    prefixes?: RepeatedMessageItem<IpAddrPrefixConfigItem>;
}

/**
 * @description  IpAddrMatch ConfigItem class.
 * @author alextsg
 */
export class IpAddrMatchConfigItem extends MessageItem<IIpAddrMatchConfig> {
    public static ajsDependencies = [
        'schemaService',
        'stringService',
        'l10nService',
    ];

    private matchOperationLabelsHash: TEnumValueLabelsHash;

    private l10nService: L10nService;

    constructor(args = {}) {
        const extendedArgs = {
            objectType: 'IpAddrMatch',
            ...args,
        };

        super(extendedArgs);

        const schemaService = this.getAjsDependency_('schemaService');

        this.matchOperationLabelsHash = schemaService.getEnumValueLabelsHash('MatchOperation');
        this.l10nService = this.getAjsDependency_('l10nService');
    }

    /** @override */
    // eslint-disable-next-line no-underscore-dangle
    public get defaultConfigOverride_(): Partial<IIpAddrMatchConfig> {
        return {
            match_criteria: MatchOperation.IS_IN,
            group_refs: [],
        };
    }

    /**
     * Returns the label for this.config.match_criteria.
     * @returns {string}
     */
    private get matchCriteriaLabel(): string {
        const { match_criteria: matchCriteria } = this.config;

        return this.matchOperationLabelsHash[matchCriteria];
    }

    /**
     * Returns an array of all addresses in this.config.
     */
    public get addresses(): string[] {
        const { addrs, ranges, prefixes } = this.config;
        const repeatedConfigItems = [addrs, ranges, prefixes];

        return repeatedConfigItems.reduce((acc, base: RepeatedMessageItem<any>) => {
            const labels = base.config.map(configItem => configItem.toString()) || [];

            return acc.concat(labels);
        }, []);
    }

    /**
     * Returns an array of names of the group_refs.
     */
    public get groupNames(): string[] {
        const stringService: StringService = this.getAjsDependency_('stringService');
        const { group_refs: groupRefs = [] } = this.config;

        return groupRefs.map(ref => stringService.name(ref));
    }

    /**
     * Label of match. Commonly used as part of expander info.
     */
    public get matchLabel(): string {
        return this.l10nService.getMessage(l10nKeys.clientIpLabel);
    }

    /**
     * Value of match to represent the data. Commonly used as part of expander info.
     */
    public get matchValue(): string {
        return this.addresses.concat(this.groupNames).join(', ');
    }

    /**
     * Returns a string representation of the config data.
     */
    public toString(): string {
        const values = this.addresses.concat(this.groupNames).join(', ');

        return `${this.matchCriteriaLabel} ${values}`;
    }

    public removeAll(): void {
        const {
            addrs,
            prefixes,
            ranges,
        } = this.config;

        const repeatedMessageItems = [addrs, prefixes, ranges];

        repeatedMessageItems.forEach(repeatedMessageItem => repeatedMessageItem.removeAll());

        delete this.config.group_refs;
    }

    public addGroupRef(ref: string): void {
        this.config.group_refs = this.config.group_refs || [];

        this.config.group_refs.push(ref);
    }

    /** @override */
    public canFlatten(): boolean {
        const {
            addrs,
            prefixes,
            ranges,
            group_refs: groupRefs,
        } = this.config;

        return addrs && !addrs.isEmpty() || prefixes && !prefixes.isEmpty() ||
            ranges && !ranges.isEmpty() || groupRefs?.length > 0;
    }
}
